import type {
  AgentTimelineEvent,
  AssistantFileTimelineEvent,
  AssistantResourceLinkTimelineEvent,
  ChatAttachment,
} from './normalize';

export type AgentTimelineItemStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'done';

export interface AgentTimelineBaseItem {
  id: string;
  kind:
    | 'chat.user'
    | 'chat.assistant'
    | 'agent.thinking'
    | 'agent.tool'
    | 'agent.file'
    | 'agent.resource_link'
    | 'agent.error'
    | 'turn.summary';
  turnId: string;
  ts: number;
  order?: number;
  agent?: string;
  agentId?: string;
  status?: AgentTimelineItemStatus;
  raw?: unknown;
}

export interface AgentChatTimelineItem extends AgentTimelineBaseItem {
  kind: 'chat.user' | 'chat.assistant';
  content: string;
  attachments?: ChatAttachment[];
}

export interface AgentThinkingTimelineItem extends AgentTimelineBaseItem {
  kind: 'agent.thinking';
  title: string;
  detail?: string;
  status: AgentTimelineItemStatus;
}

export interface AgentToolTimelineItem extends AgentTimelineBaseItem {
  kind: 'agent.tool';
  title: string;
  target?: string;
  detail?: string;
  result?: string;
  status: AgentTimelineItemStatus;
  category: 'read' | 'write' | 'search' | 'command' | 'network' | 'permission' | 'system' | 'other';
}

export interface AgentFileTimelineItem extends AgentTimelineBaseItem {
  kind: 'agent.file';
  name: string;
  mime?: string;
  size?: number;
  fileHash?: string;
  fileKey?: string;
  fileUrl?: string;
  isImage: boolean;
  status: AgentTimelineItemStatus;
}

export interface AgentResourceLinkTimelineItem extends AgentTimelineBaseItem {
  kind: 'agent.resource_link';
  title: string;
  uri: string;
  mime?: string;
  description?: string;
  status: AgentTimelineItemStatus;
}

export interface AgentErrorTimelineItem extends AgentTimelineBaseItem {
  kind: 'agent.error';
  message: string;
  status: 'failed';
}

export interface TurnSummaryTimelineItem extends AgentTimelineBaseItem {
  kind: 'turn.summary';
  title: string;
  detail?: string;
  durationMs?: number;
  status: 'done';
}

export type AgentTimelineItem =
  | AgentChatTimelineItem
  | AgentThinkingTimelineItem
  | AgentToolTimelineItem
  | AgentFileTimelineItem
  | AgentResourceLinkTimelineItem
  | AgentErrorTimelineItem
  | TurnSummaryTimelineItem;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseJsonObject(value: string): Record<string, unknown> | null {
  if (!value || value[0] !== '{') return null;
  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return undefined;
}

function basename(path: string): string {
  const clean = path.split('?')[0]?.split('#')[0] ?? path;
  return clean.split('/').filter(Boolean).pop() || clean || 'file';
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function titleCase(value: string): string {
  return value
    .replace(/^mcp__[^_]+__/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function toolStatus(phase: string, payload: Record<string, unknown> | null): AgentTimelineItemStatus {
  const status = firstString(payload?.status, payload?.state)?.toLowerCase();
  if (phase === 'error' || status === 'failed' || status === 'error') return 'failed';
  if (phase === 'tool_result' || status === 'completed' || status === 'succeeded' || status === 'success') {
    return 'succeeded';
  }
  if (status === 'pending' || status === 'queued') return 'pending';
  return 'running';
}

function toolCategory(tool: string): AgentToolTimelineItem['category'] {
  const t = tool.toLowerCase();
  if (t.includes('read') || t === 'glob' || t === 'grep' || t.includes('search_file')) return 'read';
  if (t.includes('write') || t.includes('edit') || t.includes('save') || t === 'artifact_updated') return 'write';
  if (t.includes('websearch') || t.includes('search')) return 'search';
  if (t.includes('bash') || t.includes('command') || t.includes('run')) return 'command';
  if (t.includes('webfetch') || t.includes('fetch') || t.includes('http')) return 'network';
  if (t.includes('permission')) return 'permission';
  if (t.includes('turn') || t.includes('task') || t.includes('sandbox') || t.includes('plan')) return 'system';
  return 'other';
}

function toolTarget(tool: string, payload: Record<string, unknown> | null, content: string): string | undefined {
  const input = isRecord(payload?.input) ? payload.input : payload;
  const path = firstString(
    input?.file_path,
    input?.filePath,
    input?.path,
    input?.filename,
    payload?.file_path,
    payload?.filePath,
    payload?.filename,
  );
  if (path) return basename(path);

  const command = firstString(input?.command, payload?.command);
  if (command) return command.length > 72 ? `${command.slice(0, 69)}...` : command;

  const query = firstString(input?.query, input?.pattern, payload?.query, payload?.pattern);
  if (query) return query.length > 72 ? `${query.slice(0, 69)}...` : query;

  const url = firstString(input?.url, input?.uri, payload?.url, payload?.uri);
  if (url) return hostFromUrl(url);

  if (tool === 'artifact_updated') {
    const filename = firstString(payload?.filename);
    if (filename) return basename(filename);
  }

  if (content && content[0] !== '{') return content.length > 72 ? `${content.slice(0, 69)}...` : content;
  return undefined;
}

function toolResult(payload: Record<string, unknown> | null, content: string): string | undefined {
  const result = firstString(
    payload?.result,
    payload?.output,
    payload?.message,
    payload?.text,
    isRecord(payload?.content) ? payload.content.text : undefined,
  );
  if (result) return result.length > 220 ? `${result.slice(0, 217)}...` : result;
  if (content && content[0] !== '{') return content.length > 220 ? `${content.slice(0, 217)}...` : content;
  return undefined;
}

function toolCallId(turnId: string, tool: string, payload: Record<string, unknown> | null, rowId?: string): string {
  const detail = isRecord(payload?.detail) ? payload.detail : null;
  const id = firstString(
    payload?.toolCallId,
    payload?.tool_call_id,
    payload?.callId,
    payload?.call_id,
    payload?.id,
    detail?.toolCallId,
    detail?.tool_call_id,
  );
  if (id) return `tool:${turnId}:${id}`;
  if (rowId) return `tool:${rowId}`;
  return `tool:${turnId}:${tool}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
}

function itemOrder(item: AgentTimelineItem): number {
  return typeof item.order === 'number' && Number.isFinite(item.order)
    ? item.order
    : item.ts * 1000;
}

function itemKindPriority(item: AgentTimelineItem): number {
  if (item.kind === 'chat.user') return 0;
  if (item.kind === 'agent.thinking') return 1;
  if (item.kind === 'agent.tool') return 2;
  if (item.kind === 'agent.file' || item.kind === 'agent.resource_link' || item.kind === 'agent.error') return 3;
  if (item.kind === 'chat.assistant') return 4;
  if (item.kind === 'turn.summary') return 5;
  return 9;
}

function compareTimelineItems(a: AgentTimelineItem, b: AgentTimelineItem): number {
  const byOrder = itemOrder(a) - itemOrder(b);
  if (byOrder !== 0) return byOrder;
  if (a.ts !== b.ts) return a.ts - b.ts;
  const byKind = itemKindPriority(a) - itemKindPriority(b);
  if (byKind !== 0) return byKind;
  return a.id.localeCompare(b.id);
}

function mergedOrder(a: AgentTimelineItem, b: AgentTimelineItem): number | undefined {
  if (typeof a.order === 'number' && typeof b.order === 'number') return Math.min(a.order, b.order);
  return typeof a.order === 'number' ? a.order : b.order;
}

export function sortAgentTimelineItems(items: AgentTimelineItem[]): AgentTimelineItem[] {
  return [...items].sort(compareTimelineItems);
}

function upsert(items: AgentTimelineItem[], item: AgentTimelineItem): AgentTimelineItem[] {
  const idx = items.findIndex((candidate) => candidate.id === item.id);
  if (idx < 0) return sortAgentTimelineItems([...items, item]);
  const next = [...items];
  next[idx] = {
    ...next[idx],
    ...item,
    ts: Math.min(next[idx].ts, item.ts),
    order: mergedOrder(next[idx], item),
  } as AgentTimelineItem;
  return sortAgentTimelineItems(next);
}

function sameAgent(
  item: AgentTimelineItem,
  event: { agent?: string; agentId?: string },
): boolean {
  if (event.agentId && item.agentId) return event.agentId === item.agentId;
  if (event.agent && item.agent) return event.agent === item.agent;
  return true;
}

function closeRunningThinkingForEvent(
  items: AgentTimelineItem[],
  event: { turnId: string; agent?: string; agentId?: string },
): AgentTimelineItem[] {
  return items.map((item) => {
    if (item.kind !== 'agent.thinking' || item.status !== 'running') return item;
    if (event.turnId && item.turnId === event.turnId) return { ...item, status: 'done' };
    if (!item.turnId && sameAgent(item, event)) return { ...item, status: 'done' };
    return item;
  });
}

function closeRunningTurnItems(
  items: AgentTimelineItem[],
  turnId: string,
  event?: { agent?: string; agentId?: string },
): AgentTimelineItem[] {
  return items.map((item) => {
    const sameTurn = turnId ? item.turnId === turnId : sameAgent(item, event ?? {});
    if (!sameTurn) return item;
    if (item.kind === 'agent.thinking' && item.status === 'running') {
      return { ...item, status: 'done' };
    }
    if (item.kind === 'agent.tool' && item.status === 'running') {
      return { ...item, status: 'succeeded' };
    }
    if (item.kind === 'chat.assistant' && item.status === 'running') {
      return { ...item, status: 'done' };
    }
    return item;
  });
}

function appendAssistantDelta(items: AgentTimelineItem[], event: Extract<AgentTimelineEvent, { type: 'message.assistant.delta' }>): AgentTimelineItem[] {
  const closed = closeRunningThinkingForEvent(items, event);
  const id = `assistant:${event.turnId || 'unknown'}:${event.agentId || event.agent || 'agent'}`;
  const isPersistedMessage = event.meta?.protocol === 'pitchpilot' && event.rowId?.startsWith('msg:');
  const idx = closed.findIndex((item) => item.id === id && item.kind === 'chat.assistant');
  if (idx < 0) {
    return upsert(closed, {
      id,
      kind: 'chat.assistant',
      turnId: event.turnId,
      ts: event.ts,
      order: event.order,
      agent: event.agent,
      agentId: event.agentId,
      content: event.delta,
      status: isPersistedMessage ? 'done' : 'running',
      raw: isPersistedMessage ? { persistedMessage: true, rowId: event.rowId } : undefined,
    });
  }
  const next = [...closed];
  const item = next[idx] as AgentChatTimelineItem;
  const raw = item.raw && isRecord(item.raw) ? item.raw : null;
  if (raw?.persistedMessage && !isPersistedMessage) return closed;
  next[idx] = {
    ...item,
    content: isPersistedMessage ? event.delta : `${item.content}${event.delta}`,
    status: isPersistedMessage ? 'done' : 'running',
    raw: isPersistedMessage ? { persistedMessage: true, rowId: event.rowId } : item.raw,
  };
  return sortAgentTimelineItems(next);
}

function fileItem(event: AssistantFileTimelineEvent): AgentFileTimelineItem {
  return {
    id: `file:${event.rowId || `${event.turnId}:${event.name}:${event.ts}`}`,
    kind: 'agent.file',
    turnId: event.turnId,
    ts: event.ts,
    order: event.order,
    agent: event.agent,
    agentId: event.agentId,
    name: event.name,
    mime: event.mime,
    size: event.size,
    fileHash: event.fileHash,
    fileKey: event.fileKey,
    fileUrl: event.fileUrl,
    isImage: event.isImage,
    status: 'done',
    raw: event,
  };
}

function resourceLinkItem(event: AssistantResourceLinkTimelineEvent): AgentResourceLinkTimelineItem {
  return {
    id: `resource:${event.rowId || `${event.turnId}:${event.uri}:${event.ts}`}`,
    kind: 'agent.resource_link',
    turnId: event.turnId,
    ts: event.ts,
    order: event.order,
    agent: event.agent,
    agentId: event.agentId,
    title: event.title,
    uri: event.uri,
    mime: event.mime,
    description: event.description,
    status: 'done',
    raw: event,
  };
}

function isChatVisible(event: AgentTimelineEvent): boolean {
  return (event.meta?.visibility ?? 'chat') === 'chat';
}

export function applyAgentTimelineEvent(
  previous: AgentTimelineItem[],
  event: AgentTimelineEvent,
): AgentTimelineItem[] {
  if (!isChatVisible(event)) {
    if (event.type === 'turn.completed') return closeRunningTurnItems(previous, event.turnId);
    return previous;
  }

  switch (event.type) {
    case 'message.user':
      for (const item of previous) {
        if (
          item.kind === 'chat.user' &&
          item.id.startsWith('user:local-') &&
          item.content === event.content &&
          Math.abs(item.ts - event.ts) < 60_000
        ) {
          return upsert(previous.filter((candidate) => candidate.id !== item.id), {
            id: `user:${event.messageId || event.rowId || event.ts}`,
            kind: 'chat.user',
            turnId: event.turnId,
            ts: Math.min(item.ts, event.ts),
            order: event.order ?? item.order,
            content: event.content,
            attachments: event.attachments || item.attachments,
            status: 'done',
          });
        }
      }
      return upsert(previous, {
        id: `user:${event.messageId || event.rowId || event.ts}`,
        kind: 'chat.user',
        turnId: event.turnId,
        ts: event.ts,
        order: event.order,
        content: event.content,
        attachments: event.attachments,
        status: 'done',
      });
    case 'message.assistant.delta':
      return appendAssistantDelta(previous, event);
    case 'message.assistant.file':
      return upsert(closeRunningThinkingForEvent(previous, event), fileItem(event));
    case 'message.assistant.resource_link':
      return upsert(closeRunningThinkingForEvent(previous, event), resourceLinkItem(event));
    case 'action': {
      const payload = parseJsonObject(event.content);
      const detail = isRecord(payload?.detail) ? payload.detail : null;
      if (event.source === 'legacy' && firstString(detail?.toolCallId, detail?.tool_call_id)) {
        return previous;
      }
      if (event.phase === 'thinking') {
        const id = `thinking:${event.turnId || event.rowId || event.ts}`;
        return upsert(previous, {
          id,
          kind: 'agent.thinking',
          turnId: event.turnId,
          ts: event.ts,
          order: event.order,
          agent: event.agent,
          agentId: event.agentId,
          title: 'Thinking',
          detail: event.content && event.content[0] !== '{' ? event.content : undefined,
          status: 'running',
          raw: payload ?? event.content,
        });
      }
      const closed = closeRunningThinkingForEvent(previous, event);
      const status = toolStatus(event.phase, payload);
      const tool = event.tool || 'tool';
      const id = toolCallId(event.turnId, tool, payload, event.rowId);
      const title = tool === 'run'
        ? status === 'failed' ? 'Run failed' : 'Running agent'
        : titleCase(tool);
      return upsert(closed, {
        id,
        kind: 'agent.tool',
        turnId: event.turnId,
        ts: event.ts,
        order: event.order,
        agent: event.agent,
        agentId: event.agentId,
        title,
        target: toolTarget(tool, payload, event.content),
        detail: event.phase === 'started' ? 'Started' : undefined,
        result: status === 'succeeded' || status === 'failed' ? toolResult(payload, event.content) : undefined,
        status,
        category: toolCategory(tool),
        raw: payload ?? event.content,
      });
    }
    case 'turn.completed': {
      const closed = closeRunningTurnItems(previous, event.turnId, event);
      return upsert(closed, {
        id: `turn:${event.turnId}:summary`,
        kind: 'turn.summary',
        turnId: event.turnId,
        ts: event.ts,
        order: event.order,
        agent: event.agent,
        agentId: event.agentId,
        title: event.summary || 'Turn completed',
        durationMs: event.durationMs,
        status: 'done',
        raw: event,
      });
    }
    case 'error':
      return upsert(closeRunningTurnItems(previous, event.turnId, event), {
        id: `error:${event.rowId || `${event.turnId}:${event.ts}`}`,
        kind: 'agent.error',
        turnId: event.turnId,
        ts: event.ts,
        order: event.order,
        agent: event.agent,
        agentId: event.agentId,
        message: event.message,
        status: 'failed',
        raw: event,
      });
  }
}
